import { quarterMilePoint } from './carFigures';
import { formatPrice, formatStampedMonth } from './carMoney';
import {
    EM_DASH,
    figureRow,
    formatSpecNumber,
    formatYearRange,
    specDatedLabelKey,
    specLabelKey,
    specRow,
} from './carProperties';

/**
 * The full specification — the table under the card (SPEC-002 PR-2.8).
 *
 * Split out of `carProperties.js` when this sheet grew from thirteen rows to
 * twenty-three, at the same seam that file was already split along once: it is
 * about PRESENTING a figure — the em dash, the grouping, the unit suppression —
 * and this is one particular arrangement of them. The plate on the hero has a
 * different arrangement and needs none of what is below.
 *
 * The arrangement is the one an eighties spec sheet printed, because that is
 * what this catalogue is a catalogue of. A period screen gave a car two columns:
 * on the left what the maker BUILT — layout, engine type, displacement,
 * compression, power and torque at their rpm, gearbox, tyres — and on the right
 * what a tester MEASURED, plus the price. The rows below are that list, ordered
 * for a phone rather than for two columns, and the split is still legible: the
 * quarter mile is the only measured figure among them, and it says which test it
 * came from by being the same number the graph above it draws.
 *
 * Like `carProperties`, it returns NUMBERS AND MESSAGE KEYS and never a resolved
 * string. `CarSpecTable` is the only thing that puts the two together.
 */

/**
 * A row whose value is a word from a closed vocabulary rather than a number.
 *
 * It names the key and stops. Composing "5-speed manual" here would be this
 * module writing English — which is why the gear count reaches the copy as a
 * SUBSTITUTION instead: "5-speed manual" reorders in most languages and the
 * whole phrase has to stay one string a translator can move around.
 */
function vocabularyRow(spec, vocabulary, value, { valueValues = null } = {}) {
    return specRow({
        key: spec,
        labelKey: specLabelKey(spec),
        value: value ? null : EM_DASH,
        valueKey: value ? `cars.${vocabulary}.${value}` : null,
        valueValues: value ? valueValues : null,
    });
}

/**
 * A figure and the engine speed it is made at — "471 hp @ 7,000 rpm".
 *
 * The rpm is what turns a power figure into a claim about an engine rather than
 * a number: 400 bhp at 4,600 rpm is a truck-derived V10 and 400 bhp at 8,000 is
 * something else entirely, and the two cars in this catalogue that best show it
 * sit four rows apart. It goes in the NOTE rather than the value because the
 * figure the reader came for is the power; the peak is where it happens.
 */
function peakRow(car, spec, { languageCode }) {
    const built = figureRow(car, spec, { languageCode });
    const rpm = car?.specs?.[spec]?.rpm;

    if (built.value === EM_DASH || !Number.isFinite(rpm)) {
        return built;
    }

    return {
        ...built,
        noteKey: 'cars.spec.atRpm',
        noteValues: { rpm: formatSpecNumber(rpm, { languageCode }) },
    };
}

/**
 * The standing quarter, read off the acceleration trace.
 *
 * The one MEASURED figure on a sheet of published ones, and the only row here
 * whose number a magazine rather than a marque is responsible for. It is not
 * stored under a path of its own: it is the marked point of the trace the graph
 * above already draws, so the row and the graph cannot disagree (see
 * `carFigures.QUARTER_MILE_MARK`).
 *
 * Its trap speed is the note, for the same reason the rpm is: the row is about
 * how long the quarter took, and how fast the car was going when it ended is
 * what places that time.
 *
 * And since SPEC-016 PR-16.2 the note also says what KIND of figure it is. The
 * comment above called this "the one MEASURED figure on a sheet of published
 * ones", and for the first fifteen traces it was; a simulated quarter mile sat
 * in the same row, in the same face, under the same label would inherit that
 * sentence without having earned it. The basis is composed into the note key,
 * so a reader comparing a Vector's 12.0 s against a Cizeta's 12.2 s can see
 * that one of those cars was at a strip and the other was in a model.
 */
function quarterMileRow(car, { languageCode }) {
    const built = figureRow(car, 'quarterMile', { languageCode });
    const point = quarterMilePoint(car?.specs);

    if (!point) {
        return built;
    }

    return {
        ...built,
        noteKey: `cars.spec.quarterMile.trap.${car.specs.acceleration.basis}`,
        noteValues: { speed: formatSpecNumber(point.kmh, { languageCode }) },
    };
}

/**
 * The compression ratio, which is a ratio and not a measurement.
 *
 * "7.8:1" is composed in the copy rather than assembled from a figure and a unit
 * key, because the ":1" is notation rather than a unit: the sheet sets units in
 * a lighter face a space away from the number, and "7.8 :1" is not a ratio. The
 * number still goes through `formatSpecNumber`, so the decimal separator follows
 * the language the way every other figure here does.
 */
function ratioRow(car, { languageCode }) {
    const ratio = car?.specs?.engine?.compressionRatio;

    return specRow({
        key: 'compressionRatio',
        labelKey: specLabelKey('compressionRatio'),
        value: Number.isFinite(ratio) ? null : EM_DASH,
        valueKey: Number.isFinite(ratio) ? 'cars.spec.compressionRatio.value' : null,
        valueValues: Number.isFinite(ratio)
            ? { ratio: formatSpecNumber(ratio, { languageCode, fractionDigits: 1 }) }
            : null,
    });
}

/**
 * A tyre size, which is the one value on this sheet that is a NAME shaped like
 * a number.
 *
 * `225/50ZR16` is not a quantity — it is not converted, not grouped, not
 * rounded, and it means nothing at all in another unit — so it is stored as the
 * string moulded into the sidewall and printed as it was stored. It stays
 * `numeric` all the same, because it is digits and it should line up with the
 * column of figures above it. The make does not: "Pirelli P Zero" set in a
 * tabular-figures face is a brand name pretending to be a measurement.
 */
function tyreRow(spec, value, { numeric = true } = {}) {
    return specRow({
        key: spec,
        labelKey: specLabelKey(spec),
        value: value || EM_DASH,
        numeric,
    });
}

/**
 * A money row, and the one place on the sheet where the LABEL carries a value.
 *
 * A price means nothing without the year it was a price in — $85,000 in 1985
 * and $85,000 in 1995 are different amounts of money — and the sheet cannot
 * borrow that year from the "Production years" row above it, because for nine
 * of the twenty-four cars the first year they were sold in the United States is
 * not the year the record leads with. The market figure has the same problem
 * pointing the other way: it is only true as of the month it was read, and a
 * value column with no date on it is the kind of thing a reader quotes back
 * three years later.
 *
 * So the qualifier goes in the label rather than beside the number. Appending
 * " (1987)" to the formatted price would mean this module choosing a bracketing
 * convention on behalf of every language added after English, immediately after
 * `formatPrice` handed that exact class of decision to `Intl` — and it would put
 * the year inside a `.figure` span that is styled as a number.
 *
 * `labelValues` is what carries it, and the module's rule survives intact: the
 * row still names a KEY and hands over substitutions, and it is still the
 * component that resolves any words. A row whose qualifier is unreadable falls
 * back to the undated key rather than rendering `Price new ({year})` at a
 * reader — a label with a hole in it is worse than a label with less in it.
 */
function priceRow(spec, price, { languageCode, when }) {
    // `carMoney` answers null for a price it cannot draw rather than the em
    // dash, because it does not know it is being drawn on a table. Turning that
    // into the sheet's own vocabulary for absence is this row's job, and it is
    // the same em dash `figureRow` shows for a figure the catalogue lacks.
    const drawn = formatPrice(price, { languageCode });
    const isDated = drawn !== null && when !== null && when !== undefined;

    return specRow({
        key: spec,
        labelKey: isDated ? specDatedLabelKey(spec) : specLabelKey(spec),
        // One placeholder name for both rows rather than `year` and `asOf`,
        // because a translator is being asked the same question twice — where
        // does the date go in this label — and two names for it would invite two
        // answers.
        labelValues: isDated ? { when } : null,
        value: drawn ?? EM_DASH,
    });
}

/**
 * The full specification, in the order the sheet draws it.
 *
 * Four groups, and the order inside each is the period sheet's:
 *
 * 1. **What it does.** Top speed, power, torque, 0–100, the standing quarter and
 *    power-to-weight — what the reader opened the sheet for.
 * 2. **What does it.** The engine, taken apart into the four values that
 *    eighties sheets ran together as "twin turbo dohc 32v V-8": where it sits,
 *    how the cylinders are arranged, how it breathes, and its cams and valves.
 *    Then displacement and compression, which are what a reader compares.
 * 3. **What carries it.** Drivetrain, gearbox, weight, length and the tyres.
 * 4. **How many, when, and what it cost.**
 *
 * Money last, and last on purpose. What a car cost and what it costs are the
 * two rows a reader will scroll to on some of these cards, and putting them
 * above the engine would quietly turn a catalogue into a price list — SPEC-003
 * makes that argument about the garage grid and it holds harder here, where the
 * numbers are actual prices. They sit under the production run because that is
 * where they explain something: 1,311 F40s and $3M is one story, and the
 * Esprit's is the opposite one.
 *
 * Every row is the same shape, so the table renders in one loop and a row can be
 * added here without touching the component.
 */
export function specSheet(car, { languageCode = 'en' } = {}) {
    const figure = (spec) => figureRow(car, spec, { languageCode });
    const peak = (spec) => peakRow(car, spec, { languageCode });
    const engine = car?.specs?.engine;
    const transmission = car?.specs?.transmission;
    const tyres = car?.specs?.tyres;
    const launchYear = car?.price?.launch?.year;

    return [
        figure('topSpeed'),
        peak('power'),
        peak('torque'),
        figure('zeroToHundred'),
        quarterMileRow(car, { languageCode }),
        figure('powerToWeight'),
        vocabularyRow('enginePosition', 'enginePosition', engine?.position),
        vocabularyRow('engineConfiguration', 'engineConfiguration', engine?.configuration),
        vocabularyRow('aspiration', 'aspiration', engine?.aspiration),
        vocabularyRow('valvetrain', 'valvetrain', engine?.valvetrain),
        figure('displacement'),
        ratioRow(car, { languageCode }),
        vocabularyRow('drivetrain', 'drivetrain', car?.specs?.drivetrain),
        // The gear count rides in as a substitution rather than as a figure of
        // its own: "5-speed manual" is one phrase, and a "Gears: 5" row beside a
        // "Gearbox: manual" one is the same fact told twice and badly.
        vocabularyRow('transmission', 'transmission', transmission?.type, {
            valueValues: Number.isFinite(transmission?.gears) ? { gears: transmission.gears } : null,
        }),
        figure('weight'),
        figure('length'),
        tyreRow('tyres', tyres?.make, { numeric: false }),
        tyreRow('tyresFront', tyres?.front),
        tyreRow('tyresRear', tyres?.rear),
        figure('production'),
        specRow({
            key: 'years',
            labelKey: specLabelKey('years'),
            value: formatYearRange(car?.yearsProduced, { languageCode }),
        }),
        priceRow('priceNew', car?.price?.launch, {
            languageCode,
            // Ungrouped, like every other year in this module: "1,987" is the
            // one grouped number here that would be wrong in every language.
            when: Number.isFinite(launchYear)
                ? formatSpecNumber(launchYear, { languageCode, grouped: false })
                : null,
        }),
        priceRow('priceMarket', car?.price?.market, {
            languageCode,
            when: formatStampedMonth(car?.price?.market?.asOf, { languageCode }),
        }),
    ];
}
